import { useCallback, useRef, useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import type { Theme } from '@mui/material/styles';
import classNames from 'classnames';
import { CircularProgress, MenuItem, Paper } from '@mui/material';
import { DateTime } from '@widgets/DateTime';
import { MoreMenu } from '@widgets/MoreMenu';
import { useDownloadMessageAttachmentStore, useThreadStore } from '@lib';
import { SendMessage } from './SendMessage';
import { Attachments } from '@widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    backgroundColor: theme.palette.background.default,
    borderWidth: 1,
    borderColor: theme.palette?.primary.dark,
    borderStyle: 'solid',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    transition: 'border .3s',
    '&:hover': {
      borderColor: theme.palette.primary.light,
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: 0,
    gap: '20px',
  },
  headerFromTo: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: '20px',
  },
  fromTo: {
    display: 'flex',
    gap: '4px',
    flexDirection: 'column',
  },
  sender: {
    flexGrow: 1,
  },
  subject: {
    margin: 0,
  },
  hasUnread: {
    backgroundColor: theme.palette.background.paper,
  },
  warning: {
    color: theme.palette.danger.dark,
  },
  messagesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingLeft: '32px',
    width: '100%',
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  messagesSpinner: {
    display: 'block',
    margin: 'auto',
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
}));

interface ThreadTileProps {
  className?: string;
  groupId: string;
  id: string;
  subject: string;
  body?: string;
  date: string;
  allRead: boolean;
  from?: string;
  to?: string;
  replyReceiverId?: string;
  onRemove: (groupId: string) => void;
  onMarkAsRead: (groupId: string) => void;
}

export const ThreadTile = (props: ThreadTileProps) => {
  const { loading, thread, getThread } = useThreadStore();
  const { downloadAttachment } = useDownloadMessageAttachmentStore();

  const [open, setOpen] = useState<boolean>(false);
  const [replyVisible, setReplyVisible] = useState<boolean>(false);
  const refClose = useRef<() => unknown>();

  const styles = useStyles();

  useEffect(() => {
    if (open) getThread(props.groupId);
  }, [open]);

  const handleMarkAsRead = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      refClose.current?.();
      props.onMarkAsRead(props.groupId);
    },
    [refClose, props.groupId, props.onMarkAsRead],
  );
  const handleReply = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      refClose.current?.();
      setReplyVisible(true);
    },
    [refClose],
  );

  const handleDelete = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      refClose.current?.();
      props.onRemove(props.groupId);
    },
    [refClose, props.groupId, props.onRemove],
  );

  return (
    <>
      <Paper className={classNames(styles.container, !props.allRead && styles.hasUnread)} component='li' onClick={() => setOpen((prev) => !prev)}>
        <div className={styles.header}>
          <div className={styles.headerFromTo}>
            <DateTime value={props.date} />
            <span className={styles.fromTo}>
              <span className={styles.sender}>From: {props.from || '<No sender>'}</span>
              <span className={styles.sender}>To: {props.to || '<No sender>'}</span>
            </span>
          </div>
          <MoreMenu closeRef={refClose}>
            <MenuItem onClick={handleMarkAsRead}>Mark as read</MenuItem>
            <MenuItem onClick={handleReply}>Reply</MenuItem>
            <MenuItem className={styles.warning} onClick={handleDelete}>
              Delete
            </MenuItem>
          </MoreMenu>
        </div>
        <h3 className={styles.subject}>{props.subject || '<No subject>'}</h3>
        {open && <div>{props.body}</div>}
      </Paper>
      {(open || replyVisible) && (
        <div className={styles.messagesContainer}>
          {open && (
            <div className={styles.messages}>
              {loading ? (
                <CircularProgress className={styles.messagesSpinner} size={24} />
              ) : (
                <>
                  {thread.map((item, idx) => (
                    <Paper className={styles.message} key={idx}>
                      <div className={styles.messageHeader}>
                        <span className={styles.fromTo}>
                          <span>From: {item.userName}</span>
                          <span>To: {item.receiverName}</span>
                        </span>
                        <DateTime value={item.createdOn} />
                      </div>
                      {item.body}
                      {item.attachments && (
                        <Attachments items={item.attachments} onDownload={(fileId) => downloadAttachment({ messageId: item.messageId, fileId })} />
                      )}
                    </Paper>
                  ))}
                </>
              )}
            </div>
          )}
          {props.replyReceiverId && (
            <SendMessage
              mode='reply'
              threadId={props.groupId}
              subject={props.subject}
              receiverId={props.replyReceiverId}
              canClose={!open && replyVisible}
              onSuccess={() => getThread(props.groupId)}
              onClose={() => setReplyVisible(false)}
            />
          )}
        </div>
      )}
    </>
  );
};
